import { gql } from "urql"

import { AO_MIN_INGESTED_AT, goldsky } from "./graphql-client"
import { AoMessage, TransactionsResponse } from "../types"
import { messageFields, parseAoMessage } from "../utils/arweave-utils"
import { isArweaveId } from "@/utils/arweave"

/**
 * WARN This query fails if both count and cursor are set
 */
const linkedMessagesQuery = (includeCount = false) => gql`
  query (
    $messageId: String!
    $limit: Int!
    $sortOrder: SortOrder!
    $cursor: String
  ) {
    transactions(
      sort: $sortOrder
      first: $limit
      after: $cursor

      tags: [{ name: "Pushed-For", values: [$messageId] }]
      ${AO_MIN_INGESTED_AT}
    ) {
      ${includeCount ? "count" : ""}
      ...MessageFields
    }
  }

  ${messageFields}
`
export async function getLinkedMessages(
  limit = 100,
  cursor = "",
  ascending: boolean,
  //
  pushedFor: string,
): Promise<[number | undefined, AoMessage[]]> {
  try {
    const result = await goldsky
      .query<TransactionsResponse>(linkedMessagesQuery(!cursor), {
        limit,
        sortOrder: ascending ? "HEIGHT_ASC" : "INGESTED_AT_DESC",
        cursor,
        //
        messageId: pushedFor,
      })
      .toPromise()
    const { data } = result

    if (!data) return [0, []]

    const { count, edges } = data.transactions
    const events = edges.map(parseAoMessage)

    return [count, events]
  } catch (error) {
    return [0, []]
  }
}

export async function getMessageById(id: string): Promise<AoMessage | null> {
  if (!isArweaveId(id)) {
    throw new Error("Invalid Arweave ID")
  }
  const { data, error } = await goldsky
    .query<TransactionsResponse>(
      gql`
        query ($id: ID!) {
          transactions(ids: [$id], ${AO_MIN_INGESTED_AT}) {
            ...MessageFields
          }
        }

        ${messageFields}
      `,
      { id },
    )
    .toPromise()

  if (error) throw new Error(error.message)

  if (!data) return null
  if (!data.transactions.edges.length) return null

  return parseAoMessage(data.transactions.edges[0])
}

/**
 * WARN This query fails if both count and cursor are set
 */
const totalOrdersQuery = (includeCount = false) => gql`
  query (
    $entityId: String!
    $limit: Int!
    $sortOrder: SortOrder!
    $cursor: String
    $spawnMethod: String!
  ) {
    transactions(
      sort: $sortOrder
      first: $limit
      after: $cursor
      recipients: [$entityId]
      tags: [{ name: "Action", values: [$spawnMethod] }]
      ${AO_MIN_INGESTED_AT}
    ) {
      ${includeCount ? "count" : ""}
      ...MessageFields
    }
  }

  ${messageFields}
`

export async function getTotalOrders(
  limit = 100,
  cursor = "",
  ascending: boolean,
  //
  entityId: string,
  spawnMethod: string,
): Promise<[number | undefined, AoMessage[]]> {
  try {
    const result = await goldsky
      .query<TransactionsResponse>(totalOrdersQuery(!cursor), {
        limit,
        sortOrder: ascending ? "HEIGHT_ASC" : "INGESTED_AT_DESC",
        cursor,
        //
        entityId,
        spawnMethod,
      })
      .toPromise()
    const { data } = result

    if (!data) return [0, []]

    const { count, edges } = data.transactions
    const events = edges.map(parseAoMessage)

    return [count, events]
  } catch (error) {
    return [0, []]
  }
}

/**
 * WARN This query fails if both count and cursor are set
 */
const agentTxnsQuery = gql`
  query (
    $entityId: String!
  ) {
    transactions(
      recipients: [$entityId]
      tags: [{ name: "Action", values: ["Order-Confirmation"] }]
      ${AO_MIN_INGESTED_AT}
    ) {
      count
    }
  }
`

export async function getAgentTxns(entityId: string): Promise<number> {
  try {
    const result = await goldsky
      .query<TransactionsResponse>(agentTxnsQuery, {
        entityId,
      })
      .toPromise()
    const { data } = result

    if (!data) return 0

    const { count } = data.transactions
    return count || 0
  } catch (error) {
    return 0
  }
}

const last24hrQuery = gql`
  query ($entityId: String!, $sortOrder: SortOrder!, $minIngestedAt: Int!) {
    transactions(
      recipients: [$entityId]
      tags: [{ name: "Action", values: ["Order-Confirmation"] }]
      sort: $sortOrder
      first: 1000
      ingested_at: { min: $minIngestedAt }
    ) {
      ...MessageFields
    }
  }

  ${messageFields}
`

export async function getLastTransactions(entityId: string): Promise<AoMessage[]> {
  const oneDayAgo = parseInt(String(new Date(Date.now() - 24 * 60 * 60 * 1000).getTime() / 1000))

  const result = await goldsky
    .query<TransactionsResponse>(last24hrQuery, {
      entityId,
      sortOrder: "INGESTED_AT_DESC",
      minIngestedAt: oneDayAgo,
    })
    .toPromise()

  const edges = result.data?.transactions?.edges || []
  return edges.map(parseAoMessage)
}
